import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Gavel, Lock, Zap, X } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { toast } from "sonner";
import { useIdVerification } from "@/hooks/useIdVerification";
import KycRequiredModal from "@/components/kyc/KycRequiredModal";
import { authLabels, carDetailsLabels } from "@/i18n";
import { useLanguage } from "@/context/LanguageContext";
import { ListingStatus } from "@/types/listing";
import { Timer } from "lucide-react";
import {
  autoBidBaseAmount,
  ceilingToSliderSteps,
  maxAutoBidCeiling,
  minAutoBidCeiling,
  resolveAutoBidCeiling,
  sliderStepsToCeiling,
} from "@/utils/autoBidCeiling";
import { AnimatedAutoBidInput } from "./AnimatedAutoBidInput";
import { AutoBidOverBuyNowSheet } from "./AutoBidOverBuyNowSheet";
import { AutoBidAdjustedSheet } from "./AutoBidAdjustedSheet";

export type AutomaticBidState = {
  id: string;
  maxAmount: number;
  isActive: boolean;
} | null;

interface BiddingButtonsProps {
  onPlaceBid: (bidAmount: number) => void;
  onBuyNow?: () => void;
  onSetAutoBid?: (maxAmount: number) => Promise<void> | void;
  onCancelAutoBid?: () => Promise<void> | void;
  automaticBid?: AutomaticBidState;
  isAutoBidLoading?: boolean;
  minimumBidIncrement?: number;
  nextMinBid: number;
  currentBuyNowPrice?: number;
  canBuyNow?: boolean;
  currentBid: number;
  isOwnAuction?: boolean;
  isPlacingBid?: boolean;
  status?: ListingStatus;
  guestLocked?: boolean;
  onGuestLoginRequired?: () => void;
  /** Timer hit 0 but backend has not finalized status yet. */
  isFinalizing?: boolean;
}

const guestLockedButtonClass =
  "bg-muted text-muted-foreground border border-border hover:bg-muted/90 cursor-pointer shadow-none";

const BiddingButtons = ({
  onPlaceBid,
  onBuyNow,
  onSetAutoBid,
  onCancelAutoBid,
  automaticBid = null,
  isAutoBidLoading = false,
  minimumBidIncrement = 500,
  nextMinBid,
  currentBuyNowPrice,
  canBuyNow = true,
  currentBid,
  isOwnAuction = false,
  isPlacingBid = false,
  status,
  guestLocked = false,
  onGuestLoginRequired,
  isFinalizing = false,
}: BiddingButtonsProps) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const isMobile = useIsMobile();
  const [autoPanelOpen, setAutoPanelOpen] = useState(false);
  const [autoBidMax, setAutoBidMax] = useState(
    automaticBid?.maxAmount ?? nextMinBid,
  );
  const [animateTarget, setAnimateTarget] = useState<number | null>(null);
  const [pendingAdjusted, setPendingAdjusted] = useState<{
    original: number;
    adjusted: number;
  } | null>(null);
  const [showAdjustedSheet, setShowAdjustedSheet] = useState(false);
  const [showOverBuyNowSheet, setShowOverBuyNowSheet] = useState(false);
  const [overBuyNowRounded, setOverBuyNowRounded] = useState<number | null>(
    null,
  );
  const { isApproved, needsVerification } = useIdVerification();
  const [showKycModal, setShowKycModal] = useState(false);
  const [kycAction, setKycAction] = useState<"bid" | "buy">("bid");
  const { language } = useLanguage();
  const authLbl = useMemo(() => authLabels(language), [language]);
  const biLabels = carDetailsLabels(language).biddingInfo;
  const guestLabels = carDetailsLabels(language).guest;

  const isReserved = status === "reserved";
  const isDisabled =
    guestLocked || isOwnAuction || isPlacingBid || isReserved || isFinalizing;
  const canOpenAutoBid = status === "active" && !isFinalizing;
  const hasActiveAutoBid = Boolean(automaticBid?.isActive);
  const startingPrice = nextMinBid - minimumBidIncrement;
  const baseAmount = autoBidBaseAmount(currentBid, startingPrice);
  const fmt = (n: number) => n.toLocaleString("sv-SE");

  const sliderMaxSteps = useMemo(() => {
    const maxCeil = maxAutoBidCeiling(
      currentBid,
      startingPrice,
      minimumBidIncrement,
      currentBuyNowPrice,
      canBuyNow,
    );
    return Math.max(
      1,
      ceilingToSliderSteps(
        maxCeil,
        currentBid,
        startingPrice,
        minimumBidIncrement,
      ),
    );
  }, [
    currentBid,
    startingPrice,
    minimumBidIncrement,
    currentBuyNowPrice,
    canBuyNow,
  ]);

  const sliderSteps = useMemo(() => {
    const steps = ceilingToSliderSteps(
      autoBidMax,
      currentBid,
      startingPrice,
      minimumBidIncrement,
    );
    return Math.min(sliderMaxSteps, Math.max(1, steps));
  }, [
    autoBidMax,
    currentBid,
    startingPrice,
    minimumBidIncrement,
    sliderMaxSteps,
  ]);

  useEffect(() => {
    if (!hasActiveAutoBid) {
      setAutoBidMax(
        minAutoBidCeiling(currentBid, startingPrice, minimumBidIncrement),
      );
    }
  }, [
    nextMinBid,
    hasActiveAutoBid,
    currentBid,
    startingPrice,
    minimumBidIncrement,
  ]);

  useEffect(() => {
    if (automaticBid?.isActive) {
      setAutoBidMax(automaticBid.maxAmount);
    }
  }, [automaticBid?.id, automaticBid?.maxAmount, automaticBid?.isActive]);

  const promptLogin = () => onGuestLoginRequired?.();

  const guardBidAction = (): boolean => {
    if (guestLocked) {
      promptLogin();
      return false;
    }
    if (isOwnAuction) {
      toast.error(biLabels.cannotBidOwn, {
        description: biLabels.cannotBidOwnDesc,
      });
      return false;
    }
    if (needsVerification) {
      setKycAction("bid");
      setShowKycModal(true);
      return false;
    }
    return true;
  };

  const handlePlaceBid = (bidAmount: number) => {
    if (!guardBidAction()) return;

    if (bidAmount < nextMinBid) {
      toast.error(biLabels.bidMinAmount.replace("{amount}", fmt(nextMinBid)), {
        description: biLabels.bidMinDesc,
      });
      return;
    }

    onPlaceBid(bidAmount);
  };

  const submitAutoBid = async (amount: number) => {
    try {
      await onSetAutoBid?.(amount);
      setAutoPanelOpen(false);
      setShowAdjustedSheet(false);
      setPendingAdjusted(null);
    } finally {
      setAnimateTarget(null);
    }
  };

  const handleActivateAutoBid = () => {
    if (!guardBidAction()) return;

    const original = autoBidMax;
    const result = resolveAutoBidCeiling(original, {
      currentBid,
      startingPrice,
      increment: minimumBidIncrement,
      buyNowPrice: currentBuyNowPrice,
      canBuyNow,
    });

    if (!result.ok) {
      if (result.reason === "below_min") {
        toast.error(
          biLabels.autoBidMinAmount.replace(
            "{amount}",
            fmt(
              minAutoBidCeiling(currentBid, startingPrice, minimumBidIncrement),
            ),
          ),
        );
        return;
      }
      setOverBuyNowRounded(
        result.reason === "rounded_over_buy_now"
          ? (result.normalized ?? null)
          : null,
      );
      setShowOverBuyNowSheet(true);
      return;
    }

    if (result.wasAdjusted) {
      setPendingAdjusted({
        original,
        adjusted: result.normalized,
      });
      setAnimateTarget(result.normalized);
      return;
    }

    void submitAutoBid(result.normalized);
  };

  const handleCancelAutoBid = async () => {
    await onCancelAutoBid?.();
    setAutoPanelOpen(false);
  };

  const handleBuyNow = () => {
    if (guestLocked) {
      promptLogin();
      return;
    }
    if (isOwnAuction) {
      toast.error(biLabels.cannotBuyOwn, {
        description: biLabels.cannotBuyOwnDesc,
      });
      return;
    }

    if (isReserved) {
      toast.error(biLabels.alreadyReserved, {
        description: biLabels.alreadyReservedDesc,
      });
      return;
    }

    if (needsVerification || !isApproved) {
      setKycAction("buy");
      setShowKycModal(true);
      return;
    }
    onBuyNow?.();
  };

  const handleSliderChange = (values: number[]) => {
    const steps = values[0] ?? 1;
    setAutoBidMax(
      sliderStepsToCeiling(
        steps,
        currentBid,
        startingPrice,
        minimumBidIncrement,
      ),
    );
  };

  if (guestLocked) {
    return (
      <div className="space-y-2">
        <Button
          type="button"
          className={`w-full gap-1.5 ${isMobile ? "h-12" : "h-11"} ${guestLockedButtonClass}`}
          onClick={promptLogin}
        >
          <Lock className={isMobile ? "h-5 w-5" : "h-4 w-4"} />
          <span className="font-semibold text-sm">{guestLabels.lockedBid}</span>
        </Button>
        {currentBuyNowPrice != null && canBuyNow && (
          <Button
            type="button"
            variant="outline"
            className={`w-full gap-1.5 ${isMobile ? "h-11" : "h-10"} ${guestLockedButtonClass}`}
            onClick={promptLogin}
          >
            <Lock className={isMobile ? "h-5 w-5" : "h-4 w-4"} />
            <span className="font-medium text-sm">
              {guestLabels.lockedBuyNow}
            </span>
          </Button>
        )}
        <p className="text-xs text-center text-muted-foreground px-1">
          {guestLabels.lockedBiddingHint}
        </p>
      </div>
    );
  }

  return (
    <>
      <KycRequiredModal
        isOpen={showKycModal}
        onClose={() => setShowKycModal(false)}
        actionName={
          kycAction === "bid"
            ? authLbl.kyc?.required?.actions?.bid
            : authLbl.kyc?.required?.actions?.buy
        }
      />

      <AutoBidOverBuyNowSheet
        open={showOverBuyNowSheet}
        onOpenChange={setShowOverBuyNowSheet}
        buyNowPrice={currentBuyNowPrice ?? 0}
        roundedAmount={overBuyNowRounded}
      />

      <AutoBidAdjustedSheet
        open={showAdjustedSheet}
        onOpenChange={(open) => {
          setShowAdjustedSheet(open);
          if (!open) setPendingAdjusted(null);
        }}
        originalAmount={pendingAdjusted?.original ?? autoBidMax}
        adjustedAmount={pendingAdjusted?.adjusted ?? autoBidMax}
        isLoading={isAutoBidLoading}
        onConfirm={() => {
          if (pendingAdjusted) void submitAutoBid(pendingAdjusted.adjusted);
        }}
      />

      <div className="space-y-2">
        {hasActiveAutoBid && !isOwnAuction && (
          <div
            className={`flex items-center gap-1.5 text-xs font-medium px-0.5 ${
              isDark ? "text-accent" : "text-foreground/80"
            }`}
          >
            <Zap className="w-3.5 h-3.5 fill-current text-accent shrink-0" />
            <span className="tabular-nums">
              {biLabels.autoBidActiveBadgeAmount.replace(
                "{amount}",
                fmt(automaticBid!.maxAmount),
              )}
            </span>
          </div>
        )}
        <div className="flex w-full">
          <Button
            className={`${isOwnAuction ? "w-full" : "w-[62%]"} gap-1 ${
              isMobile ? "h-11" : "h-10"
            } text-black shadow-sm ${
              isOwnAuction ? "" : "rounded-r-none border-r border-r-black/10"
            } ${
              isDisabled
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-accent hover:bg-accent/90"
            }`}
            onClick={() => handlePlaceBid(nextMinBid)}
            disabled={isDisabled}
          >
            <Gavel className="w-3.5 h-3.5 shrink-0" />
            <span className="font-semibold text-xs truncate">
              {isOwnAuction
                ? biLabels.yourAuction
                : `${biLabels.bid} (${fmt(nextMinBid)} kr)`}
            </span>
          </Button>

          {!isOwnAuction && (
            <Button
              type="button"
              className={`w-[38%] gap-1.5 px-2 ${
                isMobile ? "h-11" : "h-10"
              } rounded-l-none shadow-sm flex items-center justify-center transition-colors ${
                status !== "active"
                  ? "bg-gray-400 cursor-not-allowed text-black"
                  : autoPanelOpen || hasActiveAutoBid
                    ? isDark
                      ? "bg-accent/45 text-foreground border border-accent/45 ring-1 ring-inset ring-accent/40"
                      : "bg-accent/55 text-foreground border border-accent/55 ring-1 ring-inset ring-accent/40"
                    : isDark
                      ? "bg-accent/45 text-foreground border border-accent/45 ring-1 ring-inset ring-accent/50"
                      : "bg-accent/55 text-foreground border border-accent/55 ring-1 ring-inset ring-accent/40"
              }`}
              onClick={() => setAutoPanelOpen((v) => !v)}
              disabled={!canOpenAutoBid}
            >
              <Zap
                className={`w-3.5 h-3.5 shrink-0 ${hasActiveAutoBid ? "fill-current text-accent" : ""}`}
              />
              <span className="font-semibold text-xs">{biLabels.autoBid}</span>
            </Button>
          )}
        </div>

        {autoPanelOpen && !isOwnAuction && (
          <div
            className={`rounded-lg border p-3 space-y-2.5 animate-in fade-in-0 slide-in-from-top-1 duration-200 ${
              isDark
                ? "border-accent/20 bg-black/20"
                : "border-accent/20 bg-accent/5"
            }`}
          >
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground leading-relaxed">
                {biLabels.autoBidDescription}
              </p>
              <p className="text-xs font-medium text-foreground/80">
                {biLabels.autoBidIncrementInfo.replace(
                  "{increment}",
                  fmt(minimumBidIncrement),
                )}
              </p>
            </div>

            {hasActiveAutoBid ? (
              <div className="space-y-2">
                <div
                  className={`flex items-center justify-between rounded-md px-3 py-2 text-sm ${
                    isDark ? "bg-accent/10" : "bg-white/80"
                  }`}
                >
                  <span className="text-muted-foreground">
                    {biLabels.autoBidActiveCeiling}
                  </span>
                  <span className="font-semibold tabular-nums">
                    {fmt(automaticBid!.maxAmount)} kr
                  </span>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className={`w-full h-10 gap-1.5 ${
                    isDark
                      ? "border-red-500/40 text-red-300 hover:bg-red-500/10"
                      : "border-red-500/40 text-red-700 hover:bg-red-50"
                  }`}
                  onClick={handleCancelAutoBid}
                  disabled={isAutoBidLoading || isReserved}
                >
                  <X className="w-4 h-4" />
                  <span className="font-medium text-sm">
                    {biLabels.autoBidCancel}
                  </span>
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <label className="text-xs font-medium text-muted-foreground">
                  {biLabels.autoBidMaxLabel}
                </label>
                <div className="flex w-full gap-2">
                  <AnimatedAutoBidInput
                    value={autoBidMax}
                    onChange={setAutoBidMax}
                    animateTo={animateTarget}
                    onAnimationComplete={() => {
                      setAnimateTarget(null);
                      if (pendingAdjusted) {
                        setAutoBidMax(pendingAdjusted.adjusted);
                        setShowAdjustedSheet(true);
                      }
                    }}
                    disabled={isDisabled || isAutoBidLoading}
                    placeholder={biLabels.autoBidMaxPlaceholder}
                  />
                  <Button
                    className={`h-10 px-3 ${
                      isDisabled || isAutoBidLoading || animateTarget != null
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-accent hover:bg-accent/90"
                    } text-black shadow-sm`}
                    onClick={handleActivateAutoBid}
                    disabled={
                      isDisabled || isAutoBidLoading || animateTarget != null
                    }
                  >
                    <Zap className="w-4 h-4 mr-1" />
                    <span className="font-semibold text-sm">
                      {biLabels.autoBidActivate}
                    </span>
                  </Button>
                </div>

                {sliderMaxSteps > 1 && (
                  <div className="space-y-1.5 pt-0.5">
                    <Slider
                      value={[sliderSteps]}
                      min={1}
                      max={sliderMaxSteps}
                      step={1}
                      onValueChange={handleSliderChange}
                      disabled={isDisabled || isAutoBidLoading}
                      className="w-full"
                    />
                    <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                      <span>
                        {biLabels.autoBidSliderHint.replace(
                          "{increment}",
                          fmt(minimumBidIncrement),
                        )}
                      </span>
                      <span className="tabular-nums font-medium text-foreground/70">
                        {fmt(baseAmount + sliderSteps * minimumBidIncrement)} kr
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {currentBuyNowPrice != null && canBuyNow && (
          <Button
            variant="outline"
            className={`w-full gap-1.5 ${isMobile ? "h-11" : "h-10"} ${
              isDisabled
                ? "bg-gray-400 cursor-not-allowed text-gray-600"
                : isDark
                  ? "text-white border-accent/20 hover:bg-accent/20"
                  : "text-black border-accent/20 hover:bg-accent/20"
            }`}
            onClick={handleBuyNow}
            disabled={isDisabled}
          >
            <Timer className={`${isMobile ? "w-5 h-5" : "w-4 h-4"}`} />
            <span className="font-medium text-sm sm:text-sm">
              {isOwnAuction
                ? biLabels.yourListing
                : `${biLabels.buyNow} (${fmt(currentBuyNowPrice)} kr)`}
            </span>
          </Button>
        )}
      </div>
    </>
  );
};

export default BiddingButtons;
